<template>
    <div class="card" v-show="loaded">

        <div class="card-header border-0">
            <div class="row">
                <div class="col-6">
                    <h3 class="mb-0 text-uppercase">{{ $t('common.degrees') }}</h3>
                </div>
                <div class="col-6 text-right" :class="{'pr-0': canDeleteAll}">

                    <base-button type="secondary" size="sm" @click="clearFilters" v-if="hasFilters">
                        {{ $t('common.clear-filters') }}
                    </base-button>

                    <base-button type="primary" size="sm" @click="ctrl.create()">
                        <span class="btn-inner--icon"><i class="tim-icons icon-e-add"></i></span>
                        <span class="btn-inner--text">{{ $t('degrees.new') }}</span>
                    </base-button>

                    <actions-dropdown :actions="mainActions()" v-if="canDeleteAll"></actions-dropdown>
                </div>
            </div>
        </div>

        <div class="pt-2">
            
            <table-control :paging="ctrl.paging" :search="true" v-model="search" ref="search"></table-control>

            <el-table class="table-responsive table-flush"
                    header-row-class-name="thead-light"
                    :data="ctrl.rows"
                    @sort-change="sortChange"
                    @filter-change="filterChange"
                    ref="table">

                <el-table-column :label="$t('common.name')"
                    sortable="custom" prop="name"
                >
                    <template v-slot="{row}">
                        <h4 class="mb-0">
                            <a href="#" @click.prevent="open(row)">
                                {{ row.name }}
                            </a>
                        </h4>
                        <small v-if="row.edition">
                            {{ row.edition }}
                        </small>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.code')"
                    sortable="custom" prop="code"
                >
                    <template v-slot="{row}">
                        {{ row.code }}
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.lang')"
                    column-key="lang"
                    :filters="filtering.filteringValues(langs)"
                    :filtered-value="filters.lang"
                    filter-placement="bottom-end"
                >
                    <template v-slot="{row}">
                        {{ row.lang }}
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.status')"
                    column-key="status"
                    :filters="filtering.filteringValues(allStatusOptions)"
                    :filtered-value="filters.status"
                    filter-placement="bottom-end"
                >
                    <template v-slot="{row}">
                        <badge :type="statusColor(row.status)">{{ statusLabel(row.status) }}</badge>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.owner')"
                    column-key="owner"
                    :filters="filtering.filteringValues(owners)"
                    :filtered-value="filters.owner"
                    filter-placement="bottom-end"
                >
                    <template v-slot="{row}">
                        {{ row.owner.name }}
                        <span v-if="row.shared">
                            <i class="fas fa-share ml-1"></i>
                        </span>
                    </template>
                </el-table-column>

                <el-table-column align="right" width="150">
                    <template v-slot="{row}">
                        <div class="table-actions">

                            <a href="#" class="table-action text-danger" @click.prevent="ctrl.delete(row.id)" v-if="canDelete(row)">
                                <i class="tim-icons icon-simple-remove"></i>
                            </a>

                            <a href="#" class="table-action text-primary" @click.prevent="ctrl.update(row.id)" v-if="canEdit(row)">
                                <i class="tim-icons icon-pencil"></i>
                            </a>

                            <a href="#" class="table-action text-info" @click.prevent="open(row)">
                                <i class="tim-icons icon-bullet-list-67"></i>
                            </a>
                        </div>
                    </template>
                </el-table-column>
                                
                <el-table-column align="right" width="50" class-name="actions">
                    <template v-slot="{row}">
                        <div class="table-actions">
                            <actions-dropdown :actions="actions(row)"></actions-dropdown>
                        </div>
                    </template>
                </el-table-column>

            </el-table>

            <table-pagination :paging="ctrl.paging"></table-pagination>
        </div>

        <portal to="modals">

            <form-modal v-model="ctrl.modals.edit"
                :title="$t('common.degree')"
                :loading="ctrl.form.loading"
            >
                <degree-form v-model="ctrl.form.data"
                    :errors="ctrl.form.errors"
                    :modal="ctrl.modals.edit"
                    :owners="owners"
                    :status-options="statusOptions"
                    :langs="langs"
                    :simulation-roles="simulationRoles"
                    :rating-scales="ratingScales"
                    :duplicating="ctrl.form.duplicating"
                    @next="next = true"
                >
                </degree-form>
            </form-modal>

            <confirm-modal v-model="ctrl.modals.confirmDelete">
                <p>{{ $t('degrees.delete-confirm') }}</p>
            </confirm-modal>

            <confirm-modal v-model="ctrl.modals.confirmPublish">
                <p>{{ $t('status.publish-confirm') }}</p>
            </confirm-modal>

            <confirm-modal v-model="ctrl.modals.confirmArchive">
                <p>{{ $t('status.archive-confirm') }}</p>
            </confirm-modal>

            <confirm-modal v-model="ctrl.modals.confirmRestore">
                <p>{{ $t('status.restore-confirm') }}</p>
            </confirm-modal>

        </portal>

    </div>
</template>

<script>
    import CrudController from '@trax/classes/CrudController'
    import CrudFiltering from '../../../classes/CrudFiltering'
    import DegreeForm from './DegreeForm'

    export default {

        components: {
            DegreeForm
        },

        data() {
            let filtering = new CrudFiltering('crud_degrees')
            return {
                loaded: false,
                filtering,
                ctrl: new CrudController({
                    endpoint: '/astral/api/front/degrees',
                    mapData: this.mapData,
                    unmapData: this.unmapData,
                    modals: ['confirmPublish', 'confirmArchive', 'confirmRestore'],
                    paging: {
                        perPage: 10,
                        perPageOptions: [5, 10, 25, 50],
                        total: 0
                    },
                    query: {
                        sort: ['name'],
                        include: ['status_options', 'langs', 'simulation_roles', 'rating_scales', 'owners'],
                        relations: ['owner'],
                    },
                    callbacks: {
                        created: this.thenOpen,
                        updated: this.thenOpen,
                        duplicated: this.duplicated,
                        listed: this.listed
                    },
                    errors: {
                        '423-delete': 'degrees.423-delete',
                    }
                }),
                owners: {},
                allStatusOptions: {},
                statusOptions: {},
                langs: {},
                simulationRoles: {},
                ratingScales: {},
                next: false,
                search: '',
                filters: {},
            }
        },

        computed: {

            canDeleteAll() {
                return this.$auth.hasPermission('admin.delete-archived-content')
            },

            empty() {
                return this.ctrl.rows.length ==0 && !this.search
            },

            hasFilters() {
                return Object.keys(this.filters).length > 0 || this.ctrl.query.sort[0] != 'name'
            }
        },

        created() {
            this.filtering.init(this)

            this.ctrl.init(this, 'ctrl').then(resp => {
                this.loaded = true
                this.owners = Array.isArray(resp.data.included.owners) ? {} : resp.data.included.owners
                this.langs = resp.data.included.langs
                this.simulationRoles = resp.data.included.simulation_roles
                this.ratingScales = resp.data.included.rating_scales
                this.allStatusOptions = resp.data.included.status_options
                this.statusOptions = {...this.allStatusOptions}
                delete this.statusOptions.active
                delete this.statusOptions.archived
            })
        },

        mounted() {
            if (this.filters.simpleSearch) {
                this.$refs.search.setFilter(this.filters.simpleSearch)
            }
        },

        watch: {
            search(val) {
                if (val === '') {
                    delete this.filters.simpleSearch
                } else {
                    this.filters.simpleSearch = val
                }
                this.filters = {...this.filters}    // Clone to force reactivity
                this.reload()
            }
        },

        methods: {
            
            sortChange(data) {
                this.filtering.sortChange(data)
            },

            filterChange(data) {
                this.filtering.filterChange(data)
            },

            clearFilters() {
                this.ctrl.query.sort = ['name']
                this.filtering.clearFilters()
            },

            listed() {
                this.filtering.saveUiConfig()
            },

            requestFilters() {
                let filters = {$and: []}
                
                // Lang.
                if (this.filters.lang) {
                    let inLang = {lang: {$in: this.filters.lang }}
                    filters.$and.push(inLang)
                }

                // Owner.
                if (this.filters.owner) {
                    let inOwner = {owner_id: {$in: this.filters.owner.map(id => parseInt(id)).filter(val => val) }}
                    filters.$and.push(inOwner)
                }

                // Status.
                if (this.filters.status) {
                    let inStatus = {status: {$in: this.filters.status }}
                    filters.$and.push(inStatus)
                }

                // Search.
                if (this.filters.simpleSearch) {
                    filters.$and.push({ simpleSearch: this.filters.simpleSearch})
                }

                if (filters.$and.length == 0) {
                    filters = {}
                }
                
                return filters
            },

            reload() {
                this.ctrl.filter(this.requestFilters())
            },

            shared(row) {
                return row.shared && this.$auth.user.owner_id && this.$auth.user.owner_id != row.owner_id
            },

            canEdit(row) {
                return !this.shared(row) && row.status != 'archived'
            },

            canDelete(row) {
                return !this.shared(row) && row.status != 'active' && row.status != 'archived'
            },

            canDeleteArchive(row) {
                return !this.shared(row) && this.$auth.hasPermission('admin.delete-archived-content')
            },

            canChangeStatus(row) {
                return !this.shared(row)
            },
                        
            mainActions() {
                return [{
                    label: this.$t('degrees.outdated'),
                    handler: this.deleteOutdated,
                }]
            },

            deleteOutdated() {
                this.$router.push({ name: 'clear-degrees'})
            },

            actions(row) {
                let actions = [
                    {
                        label: this.$t('common.duplicate'),
                        handler: this.duplicate,
                        data: row
                    }
                ]
                if ((row.status == 'draft' || row.status == 'validation') &&this.canChangeStatus(row)) {
                    actions.push({
                        label: this.$t('status.publish'),
                        handler: this.publish,
                        data: row
                    })
                }
                if (row.status == 'active' && this.canChangeStatus(row)) {
                    actions.push({
                        label: this.$t('status.archive'),
                        handler: this.archive,
                        data: row
                    })
                }
                if (row.status == 'archived' && this.$auth.user.admin) {
                    actions.push({
                        label: this.$t('common.restore-archive'),
                        handler: this.restoreArchive,
                        data: row
                    })
                }
                if (row.status == 'archived' && this.canDeleteArchive(row)) {
                    actions.push({
                        label: this.$t('common.delete-archive'),
                        handler: this.deleteArchive,
                        data: row
                    })
                }
                return actions
            },

            deleteArchive(row) {
                this.ctrl.delete(row.id)
            },

            duplicate(row) {
                this.ctrl.duplicate(row.id)
            },

            duplicated(resp) {
                this.$loader.hide()
            },

            publish(row) {
                this.ctrl.modals.open('confirmPublish').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        row.status = 'active'
                        this.ctrl.client.update(this.unmapData(row)).then(resp => {
                            this.ctrl.list()
                        }).catch(err => {
                            if (err.response.status == 409) {
                                this.ctrl.list()
                                this.$notify({type: 'danger', message: err.response.data.message })
                            } else {
                                this.$auth.handleErrorVm(err, this)
                            }
                        })
                    }
                })
            },

            archive(row) {
                this.ctrl.modals.open('confirmArchive').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        row.status = 'archived'
                        this.ctrl.client.update(this.unmapData(row)).then(resp => {
                            this.ctrl.list()
                        })
                    }
                })
            },

            restoreArchive(row) {
                this.ctrl.modals.open('confirmRestore').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        row.status = 'active'
                        this.ctrl.client.update(this.unmapData(row)).then(resp => {
                            this.ctrl.list()
                        })
                    }
                })
            },

            statusColor(status) {
                return {
                    draft: 'primary',
                    validation: 'warning',
                    active: 'success',
                    archived: 'secondary',
                }[status]
            },

            statusLabel(status) {
                const key = {
                    draft: 'draft',
                    validation: 'validation',
                    active: 'published',
                    archived: 'archived',
                }[status]
                return this.$t('status.'+key)
            },

            open(row) {
                this.$router.push({ name: 'admin-degree', params: { id: row.id } })
            },

            thenOpen(resp) {
                if (this.next) {
                    this.$router.push({ name: 'admin-degree', params: { id: resp.data.data.id } })
                }
            },

            mapData(degree) {
                return {
                    id: degree ? degree.id : null,
                    name: degree ? degree.name : null,
                    description: degree ? degree.description : null,
                    code: degree ? degree.code : null,
                    status: degree ? degree.status : null,
                    edition: degree ? degree.edition : null,
                    shared: degree ? degree.shared : false,
                    main_notification: degree ? degree.main_notification : null,
                    gppos_notification: degree ? degree.gppos_notification : null,
                    simulation_role_1: degree ? degree.simulation_role_1 : null,
                    simulation_role_2: degree ? degree.simulation_role_2 : null,
                    lang: degree ? degree.lang : 'en',
                    rating_scale: degree ? degree.rating_scale : '4colors',
                    go_ground: degree ? degree.go_ground : false,
                    owner: degree ? degree.owner : null,
                    owner_id: degree ? degree.owner_id : null,
                }
            },

            unmapData(data) {
                return data
            }
        }
    }
</script>

<style scoped>

    .card >>> th,
    .card >>> td {
        padding-right: 0;
    }
    .card >>> th.export,
    .card >>> td.export {
        padding-left: 0;
        text-align: center;
    }
    .card >>> th.actions,
    .card >>> td.actions {
        padding-left: 0;
        padding-right: 1.5rem;
    }

</style>
